import type * as router from '@attain-apps/fe/web/main/lib/router'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import { QueryParam } from '../../../../../query-param'
import type * as me from '../../../feature'
import type { Feature } from '../feature'

const SOURCE = 'ensureCompleteRoute (effect)'

/**
 * Syncs current entities upon changing Url
 * (e.g. selecting another tenant takes us from /portal/TestTenant1/main to
 * /portal/TestTenant2/main, so we just sync the current tenant to be
 * TestTenant2 through currentEntityPlugin)
 */
export const ensureCompleteRoute = store.effects.create(
  (feature: Feature, meFeature: me.Feature, _router: router.Router) =>
    rxjs
      .combineLatest({
        isLoadingUser: meFeature.selectors.isLoading.asObservable,
        isNavigating: _router.selectors.isNavigating.asObservable,
        rawUrl: _router.selectors.rawUrl.asObservable,
      })
      .pipe(
        rxjs.switchMap(({ isLoadingUser, isNavigating, rawUrl }) =>
          isLoadingUser || isNavigating || !_isCompletableRoute(rawUrl)
            ? rxjs.EMPTY
            : rxjs.combineLatest({
                appAlias: feature.selectors.appAlias.asObservable,
                appPageAlias: feature.selectors.appPageAlias.asObservable,
                scenarioAlias: feature.selectors.scenarioAlias.asObservable,
                tenantAlias: feature.selectors.tenantAlias.asObservable,
                zoneAlias: feature.selectors.zoneAlias.asObservable,
              }),
        ),
        rxjs.switchMap(
          ({
            appAlias,
            appPageAlias,
            scenarioAlias,
            tenantAlias,
            zoneAlias,
          }) => {
            if (!tenantAlias || !zoneAlias) {
              return feature.selectors
                .getPlatformLandingRouteDecision(tenantAlias)
                .asObservable.pipe(
                  // This effect may run before the current user's tenant/zone
                  // list has arrived. Wait for the shared landing decision so
                  // incomplete URLs do not get mistaken for no-access.
                  rxjs.filter(({ isSettled }) => isSettled),
                  // Complete this route-fix attempt after the first settled
                  // answer. Later context changes will retrigger from the
                  // router state rather than mutating this in-flight decision.
                  rxjs.take(1),
                  rxjs.map(({ url }) =>
                    url
                      ? _router.actions.navigate.from(SOURCE).create({
                          replaceUrl: true,
                          url,
                        })
                      : store.effects.SKIP,
                  ),
                )
            }
            if (zoneAlias && appAlias) {
              // If we are in an app & zone, but are missing either an app page
              // or a valid scenario, we see if we need to reroute to a
              // specific app page or scenario.
              return feature.selectors.zoneId.asObservable.pipe(
                rxjs.filter(TypeChecker.isNotNullish.raw),
                rxjs.take(1),
                rxjs.switchMap(
                  zoneId =>
                    meFeature.zoneApps.selectors.getResult(zoneId).asObservable,
                ),
                rxjs.filter(({ isSettled }) => isSettled),
                rxjs.take(1),
                rxjs.switchMap(() =>
                  feature.selectors.app.asObservable.pipe(rxjs.take(1)),
                ),
                rxjs.map(app => {
                  if (!app) {
                    return store.effects.SKIP
                  }
                  const desiredAppPageAlias =
                    appPageAlias ??
                    [...app.pages].sort((a, b) => {
                      // Displayed comes before non-displayed
                      if (!a.display) {
                        return b.display ? 1 : 0
                      }
                      if (!b.display) {
                        return -1
                      }
                      // Main comes before non-main
                      const isMainDiff =
                        (b.display.isMain ? 1 : 0) - (a.display.isMain ? 1 : 0)
                      if (isMainDiff) {
                        return isMainDiff
                      }
                      // Position is used if all else is equal
                      return (
                        (a.display?.position ?? Number.MAX_SAFE_INTEGER) -
                        (b.display?.position ?? Number.MAX_SAFE_INTEGER)
                      )
                    })[0]?.alias
                  const desiredScenarioAlias = app.supportsScenarios
                    ? // Preserve an existing scenario alias. Route-bound
                      // scenario state can refresh after process-backed
                      // creation before the zoneApps cache knows about it.
                      // Only fill a missing alias from cached scenarios.
                      (scenarioAlias ?? app.scenarios[0]?.alias)
                    : undefined
                  return desiredAppPageAlias !== appPageAlias ||
                    desiredScenarioAlias !== scenarioAlias
                    ? _router.actions.navigate.from(SOURCE).create({
                        path: [
                          '/portal',
                          tenantAlias,
                          zoneAlias,
                          appAlias,
                          desiredAppPageAlias,
                        ],
                        queryParams: {
                          plain: {
                            // If there is a scenario, we use it
                            [QueryParam.ScenarioAlias]:
                              desiredScenarioAlias ?? null,
                          },
                        },
                        replaceUrl: true,
                      })
                    : store.effects.SKIP
                }),
              )
            }
            return rxjs.EMPTY
          },
        ),
      ),
)

/**
 * Determines if the current route is even eligible for completion
 *
 * Helps prevent accidental rerouting when not intended
 */
function _isCompletableRoute(rawUrl: string): boolean {
  const path = rawUrl.startsWith('/') ? rawUrl : new URL(rawUrl).pathname
  return path === '/' || path.startsWith('/portal')
}
